import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChatBubble } from '@amzn/awsui-chat-components-console';
import {
    Box,
    Button,
    SpaceBetween,
    Alert,
    Container,
    Header,
    PromptInput,
    FileInput,
    FileTokenGroup,
    LiveRegion,
    ProgressBar,
    Badge
} from '@amzn/awsui-components-console';
import { useWebSocket } from '../../../hooks/useWebSocket';
import { useWebSocketControl as useWebSocketControlHook } from './useWebSocketControl';
import { makeApiCall } from '../../../utils/api';
import { API_PATHS } from '../../../config/api-endpoints';
import ChatBubbleAvatar from './ChatBubbleAvatar';
import MessageItem from './MessageItem';
import { ChatInterfaceProps, ChatMessage, GenerateTemplateRequest } from './types';
import { FILE_TOKEN_GROUP_I18N_STRINGS } from './constants';
import { useTemplateGeneration } from './hooks/useTemplateGeneration';
import { useTemplateActions } from './hooks/useTemplateActions';
import { useJobCreation } from './hooks/useJobCreation';
import { useChatHandlers } from './hooks/useChatHandlers';
import { useMessageSending } from './hooks/useMessageSending';
import './ChatInterface.scss';

// Helper function to generate personalized greeting content
const getGreetingContent = (userName?: string) => `👋 **Hello${userName && userName !== 'User' ? `, ${userName}` : ''}!** Welcome to ADDAPT AI Assistant.

I'm here to help you create custom data transformation templates. Here's what I can do:

🔧 **Template Generation** - Create templates from your specifications
📝 **Q&A Flow** - Answer a few questions and I'll generate a template tailored to your needs
📂 **Excel Upload** - Upload an Excel file with your data mappings for automatic template creation
💬 **Feedback & Iteration** - Refine templates based on your feedback
👁️ **Preview & Download** - View and download your generated templates

**How would you like to get started?**`;

const ChatInterface: React.FC<ChatInterfaceProps> = ({
    profileName,
    profileId,
    predefinedQuestions = [],
    uploadedFiles = [],
    onFileUpload,
    onRemoveFile,
    onProcessFile,
    onPreviewFile,
    isProcessing = false,
    isUploading = false,
    userName = 'User',
    userInitial = 'U',
    useWebSocketControl = false
}) => {
    // WebSocket integration (for non-control mode)
    const {
        messages: wsMessages,
        isConnected: wsConnected,
        isTyping,
        isLoading,
        error,
        sendMessage: sendWsMessage,
        clearMessages,
        connect,
        disconnect
    } = useWebSocket(!useWebSocketControl);
    
    // WebSocket control mode
    const {
        wsControlConnected,
        wsUiState,
        setWsUiState,
        wsSessionId,
        localMessages,
        setLocalMessages,
        sendWebSocketControlMessage,
        resetChat,
        connectWebSocketControl,
        disconnectWebSocketControl,
        allQuestions,
        setAllQuestions,
        showQuestionnaire,
        setShowQuestionnaire,
        currentQuestionIndex,
        handleQuestionAnswer
    } = useWebSocketControlHook({ userName });
    
    // Use appropriate connection status
    const isConnected = useWebSocketControl ? wsControlConnected : wsConnected;
    
    // State
    const [prompt, setPrompt] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [areFilesDragging, setAreFilesDragging] = useState(false);
    const [isDraftLoading, setIsDraftLoading] = useState(false);
    const [draftError, setDraftError] = useState<string | null>(null);
    const [draftJobId, setDraftJobId] = useState<string | null>(null);
    const [draftExpiresAt, setDraftExpiresAt] = useState<Date | null>(null);
    const [timeRemaining, setTimeRemaining] = useState<number>(60); // minutes
    const [isFullscreen, setIsFullscreen] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const promptInputRef = useRef<HTMLTextAreaElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    
    // Custom hooks for business logic
    // Create a new draft job (used by reset button and error recovery)
    const createNewDraftJob = useCallback(async () => {
        try {
            setIsDraftLoading(true);
            setDraftError(null);
            
            const response = await makeApiCall(
                API_PATHS.JOBS.GET_DRAFT,
                'POST',
                { profileId }
            );
            
            const { jobId } = response;
            const expiryTime = new Date(Date.now() + 60 * 60 * 1000); // +1 hour
            
            // Update state
            setDraftJobId(jobId);
            setDraftExpiresAt(expiryTime);
            setTimeRemaining(60);
            
            // Store in global window object
            window.__ADDAPT_TEMPLATE__ = {
                jobId,
                createdAt: Date.now()
            };
            
            return jobId;
        } catch (error: any) {
            console.error('Failed to create new draft job:', error);
            setDraftError(error.message || 'Failed to create new session. Please refresh and try again.');
            throw error;
        } finally {
            setIsDraftLoading(false);
        }
    }, [profileId]);

    const {
        generateTemplate,
        retryTemplateGeneration,
        startTemplateStatusPolling,
        stopTemplatePolling
    } = useTemplateGeneration({
        setLocalMessages,
        setWsUiState
    });
    
    const {
        handleTemplateView,
        handleTemplateDownload
    } = useTemplateActions({
        setLocalMessages,
        onPreviewFile
    });
    
    const {
        createJob
    } = useJobCreation({
        profileId,
        setLocalMessages,
        setWsUiState
    });
    
    const {
        handleFileUploadAndProcess,
        handleSupportPromptClick: handleSupportPromptClickBase,
        handleCopyMessage
    } = useChatHandlers({
        wsUiState,
        setWsUiState,
        localMessages,
        setLocalMessages,
        allQuestions,
        currentQuestionIndex,
        handleQuestionAnswer,
        sendWebSocketControlMessage,
        generateTemplate,
        retryTemplateGeneration,
        createJob,
        handleTemplateDownload,
        resetChat,
        createNewDraftJob,
        clearMessages,
        startTemplateStatusPolling,
        onPreviewFile,
        userName,
        setIsFullscreen
    });
    
    const {
        handleSendMessage: handleSendMessageBase
    } = useMessageSending({
        wsUiState,
        setWsUiState,
        localMessages,
        setLocalMessages,
        allQuestions,
        currentQuestionIndex,
        handleQuestionAnswer,
        sendWebSocketControlMessage,
        generateTemplate,
        createJob,
        handleFileUploadAndProcess,
        onPreviewFile
    });
    
    // Combine messages
    const messages = React.useMemo(() => {
        if (useWebSocketControl) {
            return localMessages;
        }
        
        const combined: ChatMessage[] = [];
        wsMessages.forEach(msg => {
            combined.push({
                id: msg.id,
                type: msg.role === 'user' ? 'user' : 'assistant',
                content: msg.content,
                timestamp: msg.timestamp,
                status: msg.status,
                actions: msg.role === 'assistant' ? 'feedback' : undefined
            });
        });
        combined.push(...localMessages);
        return combined.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    }, [wsMessages, localMessages, useWebSocketControl]);

    // Support prompts for non-WebSocket mode
    const supportPromptItems = React.useMemo(() => {
        if (useWebSocketControl) {
            return [];
        }
        return predefinedQuestions.map((q, idx) => ({
            text: q,
            id: `prompt-${idx}`
        }));
    }, [predefinedQuestions, useWebSocketControl]);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        // Force scroll to bottom function
        const scrollToBottom = () => {
            if (messagesContainerRef.current) {
                const container = messagesContainerRef.current;
                // Force immediate scroll without animation for reliability
                container.scrollTop = container.scrollHeight;
            }
        };
        
        // Use MutationObserver to detect DOM changes
        const observer = new MutationObserver(() => {
            scrollToBottom();
        });
        
        if (messagesContainerRef.current) {
            observer.observe(messagesContainerRef.current, {
                childList: true,
                subtree: true,
                characterData: true
            });
        }
        
        // Initial scroll attempts
        scrollToBottom(); // Immediate
        requestAnimationFrame(scrollToBottom); // After next frame
        
        // Fallback timeouts for complex renders
        const timeouts = [50, 100, 200, 300, 500].map(delay => 
            setTimeout(scrollToBottom, delay)
        );
        
        return () => {
            observer.disconnect();
            timeouts.forEach(clearTimeout);
        };
    }, [messages, localMessages]); // Watch both message arrays
    
    // Connect WebSocket when enabled and register global functions
    useEffect(() => {
        if (useWebSocketControl) {
            connectWebSocketControl();
            
            // Register global functions for WebSocket to call
            window.__ADDAPT_STOP_POLLING__ = stopTemplatePolling;
            window.__ADDAPT_REGENERATE__ = (apiPayload: any) => {
                // Regenerate template with feedback
                generateTemplate(apiPayload.feedback);
            };
            
            return () => {
                disconnectWebSocketControl();
                stopTemplatePolling();
                delete window.__ADDAPT_STOP_POLLING__;
                delete window.__ADDAPT_REGENERATE__;
            };
        }
    }, [useWebSocketControl, connectWebSocketControl, disconnectWebSocketControl, stopTemplatePolling, generateTemplate]);

    // Initialize draft job on mount (for WebSocket control mode)
    useEffect(() => {
        if (!useWebSocketControl) return;
        
        createNewDraftJob();
    }, [useWebSocketControl, profileId]);

    // Update TTL countdown every 30 seconds
    useEffect(() => {
        if (!useWebSocketControl || !draftExpiresAt) return;
        
        const updateCountdown = () => {
            const now = new Date();
            const remaining = draftExpiresAt.getTime() - now.getTime();
            const remainingMinutes = Math.max(0, Math.floor(remaining / 60000));
            setTimeRemaining(remainingMinutes);
        };
        
        // Initial update
        updateCountdown();
        
        // Update every 30 seconds
        const interval = setInterval(updateCountdown, 30000);
        
        return () => clearInterval(interval);
    }, [useWebSocketControl, draftExpiresAt]);

    // Handle API instruction from WebSocket (new protocol)
    useEffect(() => {
        window.__ADDAPT_CALL_API__ = async (endpoint: string, payload: any, nextStep: any) => {
            
            if (endpoint === '/GenerateTemplate') {
                // Add loading message
            const loadingMessage: ChatMessage = {
                id: `loading-${Date.now()}`,
                type: 'assistant',
                    content: <Box color="text-status-inactive">Generating your template...</Box>,
                timestamp: new Date(),
                avatarLoading: true,
                status: 'sent'
            };
            setLocalMessages((prev: ChatMessage[]) => [...prev, loadingMessage]);
            
                try {
                    // Prepare request with jobId (userId extracted by backend from auth token)
            const request: GenerateTemplateRequest = {
                        jobId: window.__ADDAPT_TEMPLATE__?.jobId || payload.jobId,
                        useLegacyHandler: false,
                        ...payload
                    };
            
            // Call API
            const response = await makeApiCall(API_PATHS.TEMPLATES.GENERATE, 'POST', request);
            
            // Store job ID and request payload for status polling and retry
            window.__ADDAPT_TEMPLATE__ = {
                ...window.__ADDAPT_TEMPLATE__,
                jobId: response.jobId,
                status: response.status,
                lastRequestPayload: request // Store for retry functionality
            };
            
                    // Remove loading message
            setLocalMessages((prev: ChatMessage[]) => 
                prev.filter(msg => !msg.id.startsWith('loading-'))
            );
            
                    // Clear chips when generation starts
                    setWsUiState((prev: any) => ({
                        ...prev,
                        chips: [],
                        input_enabled: false
                    }));
            
                    // Show tracking message
                    const trackingMessage: ChatMessage = {
                        id: `template-${Date.now()}`,
                type: 'assistant',
                        content: `✅ Template generation started!\n\n⏳ Checking template status...`,
                timestamp: new Date(),
                status: 'sent'
                    };
                    setLocalMessages((prev: ChatMessage[]) => [...prev, trackingMessage]);
            
                    // Start polling if next step indicates
                    if (nextStep?.endpoint === '/GetTemplateStatus') {
            startTemplateStatusPolling(response.jobId);
                    }
        } catch (error: any) {
            console.error('GenerateTemplate API error:', error);
            
                    // Remove loading message
                    setLocalMessages((prev: ChatMessage[]) => 
                        prev.filter(msg => !msg.id.startsWith('loading-'))
                    );
                    
                    // Show error
                    const errorMessage: ChatMessage = {
                    id: `error-${Date.now()}`,
                    type: 'system',
                        content: `❌ Failed to generate template: ${error.message || error}`,
                    timestamp: new Date(),
                    status: 'sent'
                    };
                    setLocalMessages((prev: ChatMessage[]) => [...prev, errorMessage]);
                }
            }
        };
        
        return () => {
            delete window.__ADDAPT_CALL_API__;
        };
    }, [profileId, setLocalMessages, startTemplateStatusPolling]);

    // Wrapper for handleSendMessage to work with event and clear state
    const handleSendMessage = async ({ detail: { value } }: { detail: { value: string } }) => {
        const hasText = value?.trim();
        const hasFiles = files.length > 0;
        
        if (!hasText && !hasFiles) return;
        
        if (useWebSocketControl) {
            const result = await handleSendMessageBase({ detail: { value } }, files);
            if (result?.shouldClearInput) setPrompt('');
            if (result?.shouldClearFiles) setFiles([]);
        } else {
            if (!isConnected) return;
            
            const documentContext = files.length > 0 ? {
                profileId,
                profileName,
                files: files.map(f => ({
                    name: f.name,
                    size: f.size,
                    type: f.type
                }))
            } : undefined;
            
            sendWsMessage(value, documentContext);
        setPrompt('');
        setFiles([]);
        }
    };

    // Wrapper for handleSupportPromptClick to handle view template action
    const handleSupportPromptClick = (detail: any) => {
        // Handle view template action specifically
        if (detail.detail?.id === 'view-template') {
            // Exit fullscreen mode before showing preview modal
            setIsFullscreen(false);
            handleTemplateView();
            return;
        }
        
        // For non-WebSocket mode, handle predefined questions
        if (!useWebSocketControl) {
            const question = supportPromptItems.find((item: any) => item.id === detail.detail?.id)?.text;
            if (question) {
                setPrompt(question);
                handleSendMessage({ detail: { value: question } });
            }
            return;
        }
        
        // Pass to base handler for WebSocket control mode
        handleSupportPromptClickBase(detail.detail ? detail : { detail });
    };

    // Get color for TTL countdown based on time remaining
    const getCountdownColor = (minutes: number): string => {
        if (minutes > 30) return '#16c60c'; // Green
        if (minutes >= 15) return '#ff9900'; // Orange/Yellow
        return '#d91515'; // Red
    };


    const latestMessage = messages[messages.length - 1];

    // Show loading state while initializing draft job
    if (useWebSocketControl && isDraftLoading) {
        return (
            <div className="chat-interface-container">
                <Container fitHeight>
                    <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
                        <SpaceBetween size="m" direction="vertical" alignItems="center">
                            <Box fontSize="heading-l" color="text-status-info">
                                ⏳
                            </Box>
                            <Box variant="h3">Initializing chat session...</Box>
                            <Box variant="p" color="text-body-secondary">
                                Preparing your session...
                            </Box>
                        </SpaceBetween>
                    </Box>
                </Container>
            </div>
        );
    }

    // Show error state if draft job creation failed
    if (useWebSocketControl && draftError) {
        return (
            <div className="chat-interface-container">
                <Container fitHeight>
                    <Box padding={{ vertical: 'xxxl' }}>
                        <Alert
                            type="error"
                            header="Failed to initialize session"
                        >
                            {draftError}
                            <Box margin={{ top: 's' }}>
                                <Button
                                    onClick={() => window.location.reload()}
                                    variant="primary"
                                >
                                    Refresh Page
                                </Button>
                            </Box>
                        </Alert>
                    </Box>
                </Container>
            </div>
        );
    }

    // Always show chat interface for sequential question flow
    return (
        <div 
            ref={chatContainerRef}
            className={`chat-interface-container ${isFullscreen ? 'chat-fullscreen' : ''}`}
        >
            <Container
                fitHeight
                header={
                    <Header 
                        variant="h2"
                        description={
                            <span style={{ opacity: 0.6 }}>
                                This feature is experimental and may behave differently at times.
                            </span>
                        }
                        actions={
                            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                                <div className="connection-indicator">
                                    <span 
                                        className={`status-dot ${isConnected ? 'connected' : 'connecting'}`}
                                        style={{ 
                                            width: '8px', 
                                            height: '8px', 
                                            backgroundColor: isConnected ? '#16c60c' : '#ff9900', 
                                            borderRadius: '50%', 
                                            display: 'inline-block'
                                        }} 
                                        title={isConnected ? 'Connected' : 'Connecting...'}
                                    />
                                </div>
                                {useWebSocketControl && draftJobId && (
                                    <>
                                        <style>{`
                                            @keyframes countdown-pulse {
                                                0%, 100% {
                                                    opacity: 1;
                                                    transform: scale(1);
                                                }
                                                50% {
                                                    opacity: 0.4;
                                                    transform: scale(0.98);
                                                }
                                            }
                                            .countdown-timer {
                                                animation: countdown-pulse 2s ease-in-out infinite;
                                            }
                                        `}</style>
                                        <div 
                                            className="countdown-timer"
                                            style={{ 
                                                display: 'flex', 
                                                alignItems: 'center',
                                                gap: '4px',
                                                padding: '2px 8px',
                                                borderRadius: '4px',
                                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                                fontSize: '12px',
                                                fontWeight: 500
                                            }}
                                            title={`Draft session expires in ${timeRemaining} minutes. Complete your template before time runs out.`}
                                        >
                                            <span style={{ color: getCountdownColor(timeRemaining) }}>
                                                {timeRemaining}m
                                            </span>
                                        </div>
                                    </>
                                )}
                                <Button 
                                    iconName="refresh" 
                                    variant="icon"
                                    onClick={async () => {
                                        // Clear UI state
                                        clearMessages();
                                        setLocalMessages([]);
                                        setPrompt('');
                                        setFiles([]);
                                        
                                        if (useWebSocketControl) {
                                            resetChat(true); // Clear draft job - new one will be created
                                            
                                            // Create new draft job with fresh TTL
                                            try {
                                                await createNewDraftJob();
                                                
                                                // Show success message
                                                setLocalMessages([{
                                                    id: `reset-success-${Date.now()}`,
                                                    type: 'assistant',
                                                    content: getGreetingContent(userName),
                                                    timestamp: new Date(),
                                                    status: 'sent'
                                                }]);
                                            } catch (error) {
                                                // Error already handled in createNewDraftJob
                                                console.error('Reset failed:', error);
                                            }
                                        }
                                    }}
                                    ariaLabel="Clear chat and start new session"
                                />
                                <Button 
                                    iconName={isFullscreen ? 'shrink' : 'expand'} 
                                    variant="icon"
                                    onClick={() => setIsFullscreen(!isFullscreen)}
                                    ariaLabel={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                                />
                            </SpaceBetween>
                        }
                    >
                        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                            <span>ADDAPT Chat</span>
                            <Badge color="blue">Experimental</Badge>
                        </SpaceBetween>
                    </Header>
                }
                footer={
                    <>
                        {useWebSocketControl && wsUiState.show_progress && wsUiState.progress && (
                            <div style={{ 
                                padding: '12px 16px', 
                                borderBottom: '1px solid #e1e4e8',
                                width: '100%',
                                boxSizing: 'border-box'
                            }}>
                                <ProgressBar
                                    value={wsUiState.progress.percentage}
                                    label={wsUiState.progress.label}
                                    description={wsUiState.progress.description}
                                    variant="standalone"
                                />
                            </div>
                        )}
                        
                        <PromptInput
                            ref={promptInputRef}
                            onChange={({ detail }) => setPrompt(detail.value)}
                            onAction={handleSendMessage}
                            value={prompt}
                            actionButtonAriaLabel={isTyping ? 'Sending...' : 'Send message'}
                            actionButtonIconName="send"
                            ariaLabel="Chat input"
                            placeholder={
                                useWebSocketControl ? 
                                    wsUiState.input_placeholder :
                                    (!isConnected ? "Connecting..." : "Ask a question about your data...")
                            }
                            disabled={
                                useWebSocketControl ? 
                                    ((!wsUiState.input_enabled && files.length === 0) || wsUiState.disable_all) :
                                    (!isConnected || isTyping)
                            }
                            autoFocus
                            disableSecondaryActionsPaddings
                            secondaryActions={
                                (useWebSocketControl ? wsUiState.show_file_upload : false) ? (
                                    <Box padding={{ left: 'xxs', top: 'xs' }}>
                                        <FileInput
                                            ariaLabel="Upload Excel file"
                                            variant="icon"
                                            multiple={false}
                                            accept=".xlsx,.xls"
                                            value={files}
                                            onChange={({ detail }) => {
                                                if (detail.value.length > 0) {
                                                    setFiles([detail.value[0]]);
                                                }
                                            }}
                                        />
                                    </Box>
                                ) : undefined
                            }
                            secondaryContent={
                                files.length > 0 && (
                                    <Box>
                                        <FileTokenGroup
                                            items={files.map(file => ({ file }))}
                                            onDismiss={({ detail }) => {
                                                setFiles((files: File[]) => files.filter((_, index) => index !== detail.fileIndex));
                                                if (files.length === 1) {
                                                    promptInputRef.current?.focus();
                                                }
                                            }}
                                            limit={3}
                                            alignment="horizontal"
                                            showFileThumbnail={true}
                                            i18nStrings={FILE_TOKEN_GROUP_I18N_STRINGS}
                                        />
                                    </Box>
                                )
                            }
                        />
                    </>
                }
            >
                <div 
                    className="chat-messages-container" 
                    ref={messagesContainerRef}
                    style={{ 
                        flex: 1, 
                        overflowY: 'auto',
                        maxHeight: 'calc(100vh - 400px)'
                    }}
                >
                    <LiveRegion hidden={true} assertive={latestMessage?.type === 'system'}>
                        {latestMessage?.content}
                    </LiveRegion>
                    
                    <SpaceBetween size="m" direction="vertical">
                        {error && (
                            <Alert type="error" dismissible onDismiss={() => {}}>
                                {error}
                            </Alert>
                        )}
                        
                        {messages.map((message: ChatMessage, index: number) => (
                            <React.Fragment key={message.id}>
                                <MessageItem
                                    message={message}
                                    index={index}
                                    messages={messages}
                                    userName={userName}
                                    userInitial={userInitial}
                                    useWebSocketControl={useWebSocketControl}
                                    wsUiState={wsUiState}
                                    onCopyMessage={handleCopyMessage}
                                    onSupportPromptClick={handleSupportPromptClick}
                                />
                            </React.Fragment>
                        ))}
                        
                        {isTyping && (
                            <ChatBubble
                                avatar={
                                    <ChatBubbleAvatar 
                                        type="assistant"
                                        name="ADDAPT AI"
                                        loading={true}
                                    />
                                }
                                ariaLabel="AI is typing"
                                type="incoming"
                            >
                                <Box color="text-status-inactive">Generating response...</Box>
                            </ChatBubble>
                        )}
                        
                        <div ref={messagesEndRef} />
                    </SpaceBetween>
                </div>
            </Container>
        </div>
    );
};

export default ChatInterface;
